/**
 * PayrollPayoutsPage — the single Payroll experience in Accounts.
 *
 * Three business states are kept visually distinct and never conflated:
 *   People truth (locked / revision)  →  Accounts recognition  →  Settlement.
 * Every figure on this page is served by the backend (People evidence and the
 * payroll accounting entries). Nothing here is recalculated in React.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { accountsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  AlertTriangle, ChevronDown, Download, FileText, RefreshCw, Search,
  ShieldCheck, Users,
} from 'lucide-react';

const fmtINR = (n) =>
  n === null || n === undefined || n === '' || Number.isNaN(Number(n))
    ? '—'
    : new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: 'INR', maximumFractionDigits: 0,
      }).format(Number(n));

const Chip = ({ label, value, tone, testId }) => (
  <div className="flex items-center gap-2" data-testid={testId}>
    <span className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
    <Badge variant="outline" className={`${tone} font-medium text-[11px]`}>{value}</Badge>
  </div>
);

const TONE = {
  good: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  info: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
  warn: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  bad: 'bg-rose-500/10 text-rose-300 border-rose-500/30',
  mute: 'bg-zinc-500/10 text-zinc-300 border-zinc-600/40',
};

const Figure = ({ label, value, testId, accent = 'text-zinc-100', hint }) => (
  <div className="rounded-xl border border-zinc-800/80 bg-zinc-900/50 p-3" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className={`text-xl sm:text-2xl font-semibold mt-1 tabular-nums ${accent}`}>{value}</div>
    {hint && <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>}
  </div>
);

/** Honest, non-technical copy for every blocking/odd state the backend can return. */
const explain = (code, fallback) => ({
  PAYROLL_SERVICE_CHARGE_ACCOUNTING_NOT_READY:
    'This payroll includes Service Charge allocations. Service Charge accounting has to be '
    + 'completed before the payroll can be recognised — nothing has been recorded.',
  PAYROLL_EVIDENCE_NOT_INGESTED:
    'This payroll has not been synced from People yet. Sync the locked payroll first.',
  PAYROLL_NOT_LOCKED:
    'People has not locked this payroll yet. A locked payroll is required before Accounts '
    + 'can record anything.',
  PAYROLL_NOT_ACCOUNTING_READY:
    'People has not marked this payroll as ready for accounting yet.',
  PAYROLL_EVIDENCE_SUPERSEDED:
    'A newer payroll revision has arrived. Sync again to pick up the current one.',
  PAYROLL_ACCOUNTING_INVARIANT_FAILED:
    'The payroll figures do not balance (gross less deductions does not equal net payable), '
    + 'so nothing was recorded. People needs to confirm the payroll.',
  PAYROLL_OVERTIME_NOT_SUPPORTED:
    'This payroll includes overtime, which Accounts cannot recognise yet. Nothing was recorded.',
  PEOPLE_PAYROLL_TENANT_MISMATCH:
    'This payroll belongs to a different organisation or outlet, so it was refused.',
  OUTLET_SCOPE_DENIED:
    'You can only work with payroll for your own outlet.',
  STALE_REVISION_REJECTED:
    'That was an older payroll revision, so it was ignored. The current one is unchanged.',
  PAYROLL_FACTS_NOT_ACCEPTED_FROM_CALLER:
    'Payroll amounts always come from People, never from this screen.',
  PEOPLE_UNAVAILABLE:
    'People is not reachable right now, so payroll cannot be synced. Nothing was changed.',
}[code] || fallback || code);

const Notice = ({ tone = 'warn', title, body, testId }) => (
  <Card
    className={`border ${tone === 'bad' ? 'border-rose-500/30 bg-rose-950/30'
      : tone === 'good' ? 'border-emerald-500/30 bg-emerald-950/20'
        : 'border-amber-500/30 bg-amber-950/25'}`}
    data-testid={testId}
  >
    <CardContent className="p-4 flex items-start gap-3">
      <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${tone === 'bad' ? 'text-rose-400'
        : tone === 'good' ? 'text-emerald-400' : 'text-amber-400'}`} />
      <div className="space-y-1">
        <p className={`text-sm font-medium ${tone === 'bad' ? 'text-rose-200'
          : tone === 'good' ? 'text-emerald-200' : 'text-amber-200'}`}>{title}</p>
        {body && <p className="text-[12px] text-zinc-300 leading-relaxed">{body}</p>}
      </div>
    </CardContent>
  </Card>
);

const PayrollPayoutsPage = () => {
  const [cycleInput, setCycleInput] = useState('');
  const [outletInput, setOutletInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [showAudit, setShowAudit] = useState(false);

  const fetchLines = useCallback(async (keepNotice = false) => {
    if (!cycleInput.trim()) return;
    setLoading(true);
    setError(null);
    if (!keepNotice) setNotice(null);
    try {
      const res = await accountsAPI.getPayrollPayoutLines(cycleInput.trim(), outletInput.trim() || undefined);
      setData(res.data);
    } catch (e) {
      setError(explain(e?.response?.data?.detail?.code, e?.response?.data?.detail?.message
        || e?.response?.data?.detail || e.message || 'Could not load payroll'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [cycleInput, outletInput]);

  const act = async (kind) => {
    setBusy(kind);
    setError(null);
    setNotice(null);
    try {
      const res = kind === 'sync'
        ? await accountsAPI.syncLockedPayroll(data?.month, cycleInput.trim(), outletInput.trim() || undefined)
        : await accountsAPI.recognisePayroll(cycleInput.trim(), outletInput.trim() || undefined);
      const r = res.data;
      const noop = r.outcome === 'REPLAY_NOOP';
      setNotice({
        tone: 'good',
        title: noop ? 'Payroll already up to date'
          : kind === 'sync' ? `Payroll synced from People — revision ${r.source_revision}`
            : r.previous_recognised_revision
              ? `Correction recorded — revision ${r.previous_recognised_revision} replaced by ${r.source_revision}`
              : `Payroll recognised in Accounts — revision ${r.source_revision}`,
        body: noop
          ? 'Nothing changed. The payroll figures and the accounting entry already match People.'
          : kind === 'sync'
            ? 'Accounts has stored the locked payroll. No accounting entry, no payment and no cash movement were created by this step.'
            : 'Staff cost and the payables below are now recorded. This is recognition only — the salary has not been paid.',
      });
      await fetchLines(true);
    } catch (e) {
      const d = e?.response?.data?.detail || {};
      setError(explain(d.code, d.message || (typeof d === 'string' ? d : e.message)));
    } finally {
      setBusy(null);
    }
  };

  const lines = data?.lines || [];
  const totals = data?.totals || {};
  const ingestion = data?.ingestion || {};
  const accounting = data?.accounting || {};
  const effective = accounting.effective || {};
  const legacy = data?.legacy_cash_rows || {};
  const peopleStatus = data?.people_status;
  const cycleStatus = (data?.cycle_status || '').toUpperCase();
  const isLocked = ['LOCKED', 'PAID'].includes(cycleStatus);
  const synced = Boolean(ingestion.current_revision);
  const recognised = accounting.recognition_state === 'RECOGNISED';
  const revisions = ingestion.revisions || [];
  const stored = ingestion.current_evidence || null;
  const peopleLive = peopleStatus === 'ACTIVE';
  // Source figures: live People when reachable, otherwise the locked payroll Accounts
  // already received. Both come from the backend — nothing is recomputed here.
  const source = peopleLive
    ? {
      gross: totals.gross_amount,
      deductions: totals.deductions_total,
      net: totals.net_payable,
      serviceCharge: totals.service_charge_amount,
      employees: totals.employee_count,
      period: data?.month,
      outlet: data?.outlet_id,
      locked: isLocked,
    }
    : {
      gross: stored?.totals?.gross,
      deductions: stored?.totals?.deductions,
      net: stored?.totals?.net_payable,
      serviceCharge: stored?.totals?.service_charge,
      employees: stored?.employee_count,
      period: stored?.period_start ? stored.period_start.slice(0, 7) : data?.month,
      outlet: stored?.outlet_id,
      locked: (stored?.status || '').toLowerCase() === 'locked',
    };
  const scBlocked = Number(source.serviceCharge || 0) !== 0;

  const peopleChip = peopleLive
    ? (isLocked
      ? { value: cycleStatus === 'PAID' ? 'Locked (paid in People)' : 'Locked', tone: TONE.info }
      : { value: cycleStatus || 'Open', tone: TONE.warn })
    : (source.locked
      ? { value: 'Locked (as received)', tone: TONE.info }
      : { value: 'Not reachable', tone: TONE.bad });

  const recognitionChip = recognised
    ? { value: `Recognised · r${accounting.current_recognised_revision}`, tone: TONE.good }
    : synced ? { value: 'Synced, not recognised', tone: TONE.warn }
      : { value: 'Not synced', tone: TONE.mute };

  const corrected = recognised
    && revisions.some((r) => r.superseded_by_revision);

  const blockReason = useMemo(() => {
    if (!data) return null;
    if (peopleStatus !== 'ACTIVE') return explain('PEOPLE_UNAVAILABLE');
    if (!isLocked) return explain('PAYROLL_NOT_LOCKED');
    if (scBlocked) return explain('PAYROLL_SERVICE_CHARGE_ACCOUNTING_NOT_READY');
    return null;
  }, [data, peopleStatus, isLocked, scBlocked]);

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-6xl mx-auto" data-testid="payroll-payouts-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-zinc-100" data-testid="payroll-page-title">
            Payroll
          </h1>
          <p className="text-[12px] sm:text-sm text-zinc-400 mt-1 max-w-2xl">
            People calculates payroll. Accounts records what it means for your books — and
            keeps recognition separate from payment.
          </p>
        </div>
        <Badge variant="outline" className="hidden sm:flex bg-zinc-900/40 border-zinc-700 text-zinc-300 shrink-0">
          <ShieldCheck className="w-3 h-3 mr-1" /> People is the source of truth
        </Badge>
      </div>

      <Card className="bg-zinc-900/40 border-zinc-800">
        <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="space-y-1 flex-1 min-w-0">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Payroll cycle</label>
            <Input
              data-testid="payroll-cycle-input"
              placeholder="cycle id from People"
              value={cycleInput}
              onChange={(e) => setCycleInput(e.target.value)}
              className="w-full bg-zinc-950/40 border-zinc-800"
            />
          </div>
          <div className="space-y-1 flex-1 min-w-0">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Outlet (optional)</label>
            <Input
              data-testid="payroll-outlet-input"
              placeholder="your outlet if blank"
              value={outletInput}
              onChange={(e) => setOutletInput(e.target.value)}
              className="w-full bg-zinc-950/40 border-zinc-800"
            />
          </div>
          <Button
            onClick={() => fetchLines()}
            disabled={loading || !cycleInput.trim()}
            data-testid="payroll-fetch-button"
            className="bg-blue-600 hover:bg-blue-500 h-11 w-full sm:w-auto"
          >
            <Search className="w-4 h-4 mr-2" />
            {loading ? 'Loading…' : 'Open payroll'}
          </Button>
        </CardContent>
      </Card>

      {error && <Notice tone="bad" title="Payroll not updated" body={error} testId="payroll-error-banner" />}
      {notice && <Notice tone={notice.tone} title={notice.title} body={notice.body} testId="payroll-action-notice" />}

      {loading && (
        <div className="space-y-3" data-testid="payroll-loading-skeleton">
          <Skeleton className="h-40 w-full bg-zinc-900/40" />
          <Skeleton className="h-56 w-full bg-zinc-900/40" />
        </div>
      )}

      {!loading && data && (
        <>
          {/* ── 1. What People sent ─────────────────────────────────────── */}
          <Card className="bg-zinc-900/40 border-zinc-800" data-testid="payroll-source-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-zinc-200 flex flex-wrap items-center gap-x-4 gap-y-2">
                <span>Payroll from People</span>
                <Chip label="People truth" value={peopleChip.value} tone={peopleChip.tone} testId="chip-people-truth" />
                <Chip
                  label="Revision"
                  value={`Revision ${ingestion.current_revision ?? data.revision ?? '—'}`}
                  tone={TONE.info}
                  testId="chip-revision"
                />
                {corrected && <Chip label="History" value="Corrected" tone={TONE.warn} testId="chip-corrected" />}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 text-[12px]">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Period</div>
                  <div className="text-zinc-100 mt-1" data-testid="payroll-period">{source.period || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Outlet</div>
                  <div className="text-zinc-100 mt-1 truncate" data-testid="payroll-outlet">
                    {source.outlet || outletInput.trim() || 'All outlets'}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Employees</div>
                  <div className="text-zinc-100 mt-1 flex items-center gap-1" data-testid="payroll-employees">
                    <Users className="w-3 h-3 text-zinc-500" />{source.employees ?? '—'}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Received</div>
                  <div className="text-zinc-100 mt-1" data-testid="payroll-received-at">
                    {ingestion.updated_at ? new Date(ingestion.updated_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'Not synced yet'}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Figure label="Gross payroll" value={fmtINR(source.gross)} testId="figure-gross" />
                <Figure label="Deductions" value={fmtINR(source.deductions)} testId="figure-deductions" accent="text-rose-300" />
                <Figure label="Net salary payable" value={fmtINR(source.net)} testId="figure-net" accent="text-emerald-300" />
              </div>
              {scBlocked && (
                <Figure label="Service charge in this payroll" value={fmtINR(source.serviceCharge)} testId="figure-sc" accent="text-amber-300" />
              )}
              {!peopleLive && synced && (
                <p className="text-[11px] text-zinc-500" data-testid="payroll-source-note">
                  People is not reachable right now, so these are the locked figures Accounts
                  received. They cannot change until People responds again.
                </p>
              )}
            </CardContent>
          </Card>

          {/* ── 2. What Accounts has done with it ───────────────────────── */}
          <Card className="bg-zinc-900/40 border-zinc-800" data-testid="payroll-accounting-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-zinc-200 flex flex-wrap items-center gap-x-4 gap-y-2">
                <span>In your accounts</span>
                <Chip label="Recognition" value={recognitionChip.value} tone={recognitionChip.tone} testId="chip-recognition" />
                <Chip
                  label="Settlement"
                  value={recognised ? 'Outstanding' : 'Not applicable yet'}
                  tone={recognised ? TONE.warn : TONE.mute}
                  testId="chip-settlement"
                />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {recognised ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Figure
                      label="Staff / payroll cost"
                      value={fmtINR(effective.PAYROLL_STAFF_COST)}
                      testId="figure-staff-cost"
                      hint="Shown in the P&L for this period"
                    />
                    <Figure
                      label="Payroll deductions payable"
                      value={fmtINR(effective.PAYROLL_DEDUCTIONS_PAYABLE)}
                      testId="figure-deductions-payable"
                      accent="text-amber-300"
                      hint="Owed onward, not yet remitted"
                    />
                    <Figure
                      label="Net salary payable"
                      value={fmtINR(effective.NET_SALARY_PAYABLE)}
                      testId="figure-net-payable"
                      accent="text-emerald-300"
                      hint="Owed to staff, not yet paid"
                    />
                  </div>
                  <p className="text-[12px] text-zinc-400" data-testid="payroll-recognition-summary">
                    Total payroll recognised{' '}
                    <span className="text-zinc-100 font-semibold tabular-nums">
                      {fmtINR(effective.PAYROLL_STAFF_COST)}
                    </span>
                    {' · '}settlement <span className="text-amber-300">outstanding</span>
                    {corrected && (
                      <>
                        {' · '}
                        <span className="text-amber-300">
                          revision {revisions.find((r) => r.superseded_by_revision)?.source_revision} superseded
                          by {accounting.current_recognised_revision}
                        </span>
                      </>
                    )}
                  </p>
                  <p className="text-[11px] text-zinc-500">
                    Recognised, not paid. No bank or cash entry has been made for this payroll.
                  </p>
                </>
              ) : (
                <p className="text-[12px] text-zinc-400" data-testid="payroll-not-recognised">
                  {synced
                    ? 'This payroll has been received from People but is not yet recorded in your accounts. Recognising it books the staff cost and the payables — it does not pay anyone.'
                    : 'Sync this payroll from People first, then recognise it in your accounts.'}
                </p>
              )}

              <div className="flex flex-col sm:flex-row flex-wrap gap-2 pt-1">
                <Button
                  onClick={() => act('sync')}
                  disabled={peopleStatus !== 'ACTIVE' || !isLocked || busy !== null || !data.month}
                  data-testid="payroll-sync-locked-button"
                  variant="outline"
                  className="border-zinc-700 hover:bg-zinc-800 h-11"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${busy === 'sync' ? 'animate-spin' : ''}`} />
                  {busy === 'sync' ? 'Syncing…' : 'Sync payroll from People'}
                </Button>
                <Button
                  onClick={() => act('recognise')}
                  disabled={!synced || scBlocked || busy !== null}
                  data-testid="payroll-recognise-button"
                  className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 h-11"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {busy === 'recognise' ? 'Recording…' : recognised ? 'Update payroll in accounts' : 'Recognise payroll in accounts'}
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      const res = await accountsAPI.exportPayrollPayoutLines(cycleInput.trim(), outletInput.trim() || undefined);
                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
                      const a = document.createElement('a');
                      a.href = url; a.download = `payroll_${cycleInput.trim()}.csv`;
                      document.body.appendChild(a); a.click(); a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (e) { setError('Export failed'); }
                  }}
                  disabled={peopleStatus !== 'ACTIVE' || lines.length === 0}
                  variant="outline"
                  data-testid="payroll-export-button"
                  className="border-zinc-700 hover:bg-zinc-800 h-11"
                >
                  <Download className="w-4 h-4 mr-2" /> Export
                </Button>
              </div>

              {blockReason && (
                <p className="text-[12px] text-amber-300" data-testid="payroll-block-reason">{blockReason}</p>
              )}
              {legacy.count > 0 && (
                <p className="text-[11px] text-zinc-500" data-testid="payroll-legacy-note">
                  {legacy.count} older payroll cash rows from a retired workflow are kept for audit and
                  are not counted as payroll cost.
                </p>
              )}
            </CardContent>
          </Card>

          {/* ── 3. Audit / technical detail ─────────────────────────────── */}
          <Card className="bg-zinc-900/40 border-zinc-800" data-testid="payroll-audit-card">
            <button
              type="button"
              onClick={() => setShowAudit((v) => !v)}
              className="w-full flex items-center justify-between p-4 text-left"
              data-testid="payroll-audit-toggle"
            >
              <span className="text-sm text-zinc-200">Revision history &amp; audit detail</span>
              <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${showAudit ? 'rotate-180' : ''}`} />
            </button>
            {showAudit && (
              <CardContent className="pt-0 space-y-4">
                <div className="space-y-2" data-testid="payroll-revision-history">
                  {revisions.length === 0 && (
                    <p className="text-[12px] text-zinc-500">No payroll revisions received yet.</p>
                  )}
                  {revisions.map((r) => {
                    const isCurrent = r.source_revision === ingestion.current_revision;
                    return (
                      <div
                        key={r.source_revision}
                        className={`rounded-lg border p-3 ${isCurrent ? 'border-emerald-500/30 bg-emerald-950/20' : 'border-zinc-800 bg-zinc-950/30'}`}
                        data-testid={`payroll-revision-${r.source_revision}`}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-[12px] text-zinc-100">
                            Revision {r.source_revision}
                            {isCurrent
                              ? <span className="text-emerald-300"> · current</span>
                              : r.superseded_by_revision
                                ? <span className="text-amber-300"> · superseded by revision {r.superseded_by_revision}</span>
                                : null}
                            {accounting.current_recognised_revision === r.source_revision && (
                              <span className="text-zinc-400"> · recognised</span>
                            )}
                          </span>
                          <span className="text-[11px] text-zinc-400 tabular-nums">
                            gross {fmtINR(r.totals?.gross ?? r.totals?.gross_amount)} · net {fmtINR(r.totals?.net_payable)}
                          </span>
                        </div>
                        <div className="text-[10px] text-zinc-500 font-mono mt-1 break-all">
                          {r.source_identity} · received {r.received_at}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {(accounting.entries || []).length > 0 && (
                  <div className="space-y-2" data-testid="payroll-accounting-entries">
                    {accounting.entries.map((e) => (
                      <div key={e.entry_id} className="rounded-lg border border-zinc-800 bg-zinc-950/30 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2 text-[12px]">
                          <span className="text-zinc-100">
                            {e.entry_type === 'PAYROLL_RECOGNITION_REVERSAL' ? 'Reversal' : 'Recognition'}
                            {' '}· revision {e.source_revision}
                            <span className="text-zinc-500"> · {e.status.toLowerCase()}</span>
                          </span>
                          <span className="text-zinc-400 tabular-nums">
                            debits {fmtINR(e.total_debits)} = credits {fmtINR(e.total_credits)}
                          </span>
                        </div>
                        <div className="mt-2 space-y-1">
                          {(e.lines || []).map((ln, i) => (
                            <div key={i} className="flex justify-between text-[11px] text-zinc-400">
                              <span>{ln.side === 'DEBIT' ? 'Dr' : 'Cr'} {ln.label}</span>
                              <span className="tabular-nums">{fmtINR(ln.amount)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="text-[10px] text-zinc-600 font-mono mt-2 break-all">
                          {e.source_identity}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* ── 4. Employee lines (People evidence) ─────────────────────── */}
          <Card className="bg-zinc-900/40 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-zinc-200 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Employee payout lines
                <span className="text-[11px] text-zinc-500 font-normal">from People</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {lines.length === 0 ? (
                <div className="p-8 text-center text-sm text-zinc-500" data-testid="payroll-empty-state">
                  {peopleStatus === 'ACTIVE'
                    ? 'People returned no employee lines for this payroll.'
                    : 'No lines available — see the message above.'}
                </div>
              ) : (
                <>
                  {/* mobile: stacked rows, no horizontal scrolling */}
                  <div className="sm:hidden divide-y divide-zinc-800/60" data-testid="payroll-lines-mobile">
                    {lines.map((ln, idx) => (
                      <div key={ln.line_id || idx} className="p-4 space-y-1" data-testid={`payroll-line-row-${idx}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[13px] text-zinc-100 truncate">{ln.employee_name || ln.employee_id}</span>
                          <span className="text-[13px] text-emerald-300 tabular-nums">{fmtINR(ln.net_payable)}</span>
                        </div>
                        <div className="text-[11px] text-zinc-500">
                          gross {fmtINR(ln.gross_amount)}
                          {ln.employee_code ? ` · ${ln.employee_code}` : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="hidden sm:block overflow-x-auto">
                    <Table data-testid="payroll-lines-table">
                      <TableHeader>
                        <TableRow className="border-zinc-800">
                          <TableHead className="text-zinc-400">Employee</TableHead>
                          <TableHead className="text-right text-zinc-400">Gross</TableHead>
                          <TableHead className="text-right text-zinc-400">Service charge</TableHead>
                          <TableHead className="text-right text-zinc-400">Net payable</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {lines.map((ln, idx) => (
                          <TableRow key={ln.line_id || idx} className="border-zinc-800/60">
                            <TableCell>
                              <div className="text-zinc-100">{ln.employee_name || '—'}</div>
                              <div className="text-[11px] text-zinc-500 font-mono">{ln.employee_code || ln.employee_id}</div>
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-zinc-100">{fmtINR(ln.gross_amount)}</TableCell>
                            <TableCell className="text-right tabular-nums text-zinc-100">{fmtINR(ln.service_charge_amount)}</TableCell>
                            <TableCell className="text-right tabular-nums text-emerald-300">{fmtINR(ln.net_payable)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default PayrollPayoutsPage;
